import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Grid,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Switch,
  FormControlLabel,
  Stack,
  LinearProgress,
  Fab,
  Avatar,
  Box as MuiBox,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  DragIndicator as DragIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  ColorLens as ColorIcon,
  Close as CloseIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import api from './api';
import ColorEmojiPicker from './components/ColorEmojiPicker';
import { TAG_EMOJI_CATEGORIES } from './utils/emojiCategories';
import { AVAILABLE_LANGUAGES, DEFAULT_LANGUAGE } from './utils/languages';

const TagManager = () => {
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTag, setEditingTag] = useState(null);
  const [currentLanguage, setCurrentLanguage] = useState(DEFAULT_LANGUAGE);
  const [formData, setFormData] = useState({
    name: '',
    emoji: '🏷️',
    color: '#3B82F6',
    description: '',
    isActive: true,
    sortOrder: 0
  });
  const [submitting, setSubmitting] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewingTag, setViewingTag] = useState(null);
  const [associations, setAssociations] = useState({ foods: [], recipes: [] });
  const [associationsLoading, setAssociationsLoading] = useState(false);

  useEffect(() => {
    fetchTags();
  }, [currentLanguage]);

  const fetchTags = async () => {
    try {
      setLoading(true);
      const response = await api.get('/tags/admin/all', {
        params: { language: currentLanguage }
      });
      setTags(response.data.data || []);
      setError(null);
    } catch (err) {
      setError('Failed to fetch tags');
      console.error('Error fetching tags:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTag = () => {
    setEditingTag(null);
    setFormData({
      name: '',
      emoji: '🏷️',
      color: '#3B82F6',
      description: '',
      isActive: true,
      sortOrder: tags.length
    });
    setDialogOpen(true);
  };

  const handleEditTag = (tag) => {
    setEditingTag(tag);
    setFormData({
      name: tag.name,
      emoji: tag.emoji,
      color: tag.color,
      description: tag.description || '',
      isActive: tag.isActive,
      sortOrder: tag.sortOrder || 0
    });
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      setError('Tag name is required');
      return;
    }

    try {
      setSubmitting(true);
      
      if (editingTag) {
        await api.put(`/tags/admin/${editingTag.id}`, formData);
      } else {
        await api.post('/tags/admin', formData);
      }
      
      setDialogOpen(false);
      await fetchTags();
      setError(null);
    } catch (err) {
      setError(editingTag ? 'Failed to update tag' : 'Failed to create tag');
      console.error('Error submitting tag:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteTag = async (tagId) => {
    if (!window.confirm('Are you sure you want to delete this tag? This will remove it from all recipes.')) {
      return;
    }

    try {
      await api.delete(`/tags/admin/${tagId}`);
      await fetchTags();
      setError(null);
    } catch (err) {
      // Extract error message from the API response
      const errorMessage = err.response?.data?.error?.userMessage || 
                          err.response?.data?.error?.message || 
                          err.response?.data?.message || 
                          'Failed to delete tag';
      setError(errorMessage);
      console.error('Error deleting tag:', err);
    }
  };

  const handleToggleActive = async (tag) => {
    try {
      await api.put(`/tags/admin/${tag.id}`, {
        ...tag,
        isActive: !tag.isActive
      });
      await fetchTags();
      setError(null);
    } catch (err) {
      setError('Failed to update tag status');
      console.error('Error updating tag status:', err);
    }
  };

  const handleUpdateSortOrder = async (tagId, newSortOrder) => {
    try {
      const tag = tags.find(t => t.id === tagId);
      await api.put(`/tags/admin/${tagId}`, {
        ...tag,
        sortOrder: newSortOrder
      });
      await fetchTags();
      setError(null);
    } catch (err) {
      setError('Failed to update tag order');
      console.error('Error updating tag order:', err);
    }
  };

  // Handle viewing tag associations
  const handleViewTag = async (tag) => {
    setViewingTag(tag);
    setViewDialogOpen(true);
    setAssociationsLoading(true);
    
    try {
      const response = await api.get(`/tags/admin/${tag.id}/associations`);
      setAssociations({
        foods: response.data.data.foods || [],
        recipes: response.data.data.recipes || []
      });
      setError(null);
    } catch (err) {
      setError('Failed to fetch tag associations');
      console.error('Error fetching tag associations:', err);
    } finally {
      setAssociationsLoading(false);
    }
  };

  // Handle unassigning tag from food
  const handleUnassignFromFood = async (foodId, tagId) => {
    if (!window.confirm('Are you sure you want to remove this tag from this food?')) {
      return;
    }

    try {
      await api.delete(`/tags/food/${foodId}/${tagId}`);
      
      // Update associations by removing the unassigned food
      setAssociations(prev => ({
        ...prev,
        foods: prev.foods.filter(food => food.id !== foodId)
      }));

      // Refresh the main tags list to update counts
      await fetchTags();
      setError(null);
    } catch (err) {
      const errorMessage = err.response?.data?.error?.message || 'Failed to remove tag from food';
      setError(errorMessage);
      console.error('Error removing tag from food:', err);
    }
  };

  // Handle unassigning tag from recipe
  const handleUnassignFromRecipe = async (recipeId, tagId) => {
    if (!window.confirm('Are you sure you want to remove this tag from this recipe?')) {
      return;
    }

    try {
      await api.delete(`/tags/recipe/${recipeId}/${tagId}`);
      
      // Update associations by removing the unassigned recipe
      setAssociations(prev => ({
        ...prev,
        recipes: prev.recipes.filter(recipe => recipe.id !== recipeId)
      }));

      // Refresh the main tags list to update counts
      await fetchTags();
      setError(null);
    } catch (err) {
      const errorMessage = err.response?.data?.error?.message || 'Failed to remove tag from recipe';
      setError(errorMessage);
      console.error('Error removing tag from recipe:', err);
    }
  };

  // Helper function to get contrast color (for display purposes)
  // Helper function to get contrast color (for display purposes)
  const getContrastColor = (hex) => {
    // Convert hex to RGB
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    
    // Calculate luminance
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    
    return luminance > 0.5 ? '#000000' : '#FFFFFF';
  };

  // Helper function to get tag name for current language (from backend)
  const getTagName = (tag) => {
    if (tag.languageSpec && tag.languageSpec[currentLanguage] && tag.languageSpec[currentLanguage].name) {
      return tag.languageSpec[currentLanguage].name;
    }
    return tag.name || '';
  };

  // Helper function to get tag description for current language (from backend)
  const getTagDescription = (tag) => {
    if (tag.languageSpec && tag.languageSpec[currentLanguage] && tag.languageSpec[currentLanguage].description) {
      return tag.languageSpec[currentLanguage].description;
    }
    return tag.description || '';
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Tag Management
        </Typography>
        <LinearProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">
          Tag Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateTag}
        >
          Create Tag
        </Button>
      </Box>
      
      {/* Language Selector */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom>
          Select Language:
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
          {AVAILABLE_LANGUAGES.map((lang) => (
            <Chip
              key={lang}
              label={lang.charAt(0).toUpperCase() + lang.slice(1)}
              color={currentLanguage === lang ? "primary" : "default"}
              onClick={() => setCurrentLanguage(lang)}
              variant={currentLanguage === lang ? "filled" : "outlined"}
            />
          ))}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Statistics */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Tags
              </Typography>
              <Typography variant="h5">
                {tags.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Active Tags
              </Typography>
              <Typography variant="h5" color="success.main">
                {tags.filter(tag => tag.isActive).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Inactive Tags
              </Typography>
              <Typography variant="h5" color="warning.main">
                {tags.filter(tag => !tag.isActive).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tags Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Order</TableCell>
              <TableCell>Tag</TableCell>
              <TableCell>Name ({currentLanguage})</TableCell>
              <TableCell>Color</TableCell>
              <TableCell>Description ({currentLanguage})</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Usage</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tags
              .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
              .map((tag, index) => (
              <TableRow key={tag.id}>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <DragIcon color="action" />
                    <Typography variant="body2">
                      {tag.sortOrder || 0}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip
                    label={`${tag.emoji} ${tag.name}`}
                    sx={{
                      backgroundColor: tag.color,
                      color: getContrastColor(tag.color),
                      fontWeight: 'bold'
                    }}
                  />
                </TableCell>
                <TableCell>{getTagName(tag)}</TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 20,
                        height: 20,
                        backgroundColor: tag.color,
                        borderRadius: '50%',
                        border: '1px solid #ccc'
                      }}
                    />
                    <Typography variant="body2">
                      {tag.color}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ maxWidth: 200 }}>
                    {getTagDescription(tag) || '-'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={tag.isActive}
                        onChange={() => handleToggleActive(tag)}
                        color="primary"
                      />
                    }
                    label={tag.isActive ? 'Active' : 'Inactive'}
                  />
                </TableCell>
                <TableCell>
                  <Stack spacing={0.5}>
                    {(tag._count?.foodTags > 0 || !tag._count?.recipeTags) && (
                      <Typography variant="body2">
                        {tag._count?.foodTags || 0} foods
                      </Typography>
                    )}
                    {(tag._count?.recipeTags > 0 || !tag._count?.foodTags) && (
                      <Typography variant="body2">
                        {tag._count?.recipeTags || 0} recipes
                      </Typography>
                    )}
                  </Stack>
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={1}>
                    <Tooltip title="View Associated Foods & Recipes">
                      <IconButton
                        size="small"
                        onClick={() => handleViewTag(tag)}
                      >
                        <VisibilityIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit Tag">
                      <IconButton
                        size="small"
                        onClick={() => handleEditTag(tag)}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete Tag">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteTag(tag.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create/Edit Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingTag ? 'Edit Tag' : 'Create New Tag'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={8}>
                <TextField
                  fullWidth
                  label="Tag Name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <ColorEmojiPicker
                  color={formData.color}
                  emoji={formData.emoji}
                  onColorChange={(color) => setFormData({ ...formData, color })}
                  onEmojiChange={(emoji) => setFormData({ ...formData, emoji })}
                  emojiCategories={TAG_EMOJI_CATEGORIES}
                  previewLabel={formData.name}
                  showPreview={false}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Sort Order"
                  type="number"
                  value={formData.sortOrder}
                  onChange={(e) => setFormData({ ...formData, sortOrder: parseInt(e.target.value) || 0 })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  multiline
                  rows={3}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isActive}
                      onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    />
                  }
                  label="Active"
                />
              </Grid>
            </Grid>

            {/* Preview */}
            <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="body2" gutterBottom>
                Preview:
              </Typography>
              <Chip
                label={`${formData.emoji} ${formData.name}`}
                sx={{
                  backgroundColor: formData.color,
                  color: getContrastColor(formData.color),
                  fontWeight: 'bold'
                }}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDialogOpen(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={submitting || !formData.name.trim()}
          >
            {submitting ? 'Saving...' : (editingTag ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Tag Associations Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { 
            minHeight: '500px',
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {viewingTag && (
              <Chip
                label={`${viewingTag.emoji} ${viewingTag.name}`}
                sx={{
                  backgroundColor: viewingTag.color,
                  color: getContrastColor(viewingTag.color),
                  fontWeight: 'bold',
                  fontSize: '0.95rem',
                  height: '36px'
                }}
              />
            )}
            <Typography variant="h5" component="span" sx={{ fontWeight: 600 }}>
              Associated Items
            </Typography>
            <IconButton
              aria-label="close"
              onClick={() => setViewDialogOpen(false)}
              sx={{ 
                ml: 'auto',
                '&:hover': { bgcolor: 'grey.100' }
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ px: 3, pb: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}
          
          {associationsLoading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 4 }}>
              <LinearProgress sx={{ width: '100%', mb: 2 }} />
              <Typography variant="body2" color="textSecondary">
                Loading associations...
              </Typography>
            </Box>
          ) : (
            <Box>
              {/* Foods Section */}
              {associations.foods && associations.foods.length > 0 && (
                <Box sx={{ mb: 4 }}>
                  <Typography 
                    variant="h6" 
                    gutterBottom 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 1,
                      mb: 2,
                      color: 'primary.main',
                      fontWeight: 600
                    }}
                  >
                    🍎 Foods ({associations.foods.length})
                  </Typography>
                  <Grid container spacing={2} sx={{ justifyContent: 'flex-start' }}>
                    {associations.foods.map((food) => (
                      <Grid 
                        item 
                        xs={3} 
                        sm={3} 
                        md={3} 
                        lg={3}
                        key={food.id}
                        sx={{ 
                          display: 'flex',
                          '& > *': { 
                            flex: 1,
                            minWidth: 0 
                          }
                        }}
                      >
                        <Card 
                          sx={{ 
                            height: '180px',
                            width: '100%',
                            minWidth: '200px',
                            maxWidth: '200px',
                            display: 'flex',
                            flexDirection: 'column',
                            position: 'relative',
                            transition: 'all 0.2s ease-in-out',
                            '&:hover': { 
                              transform: 'translateY(-2px)',
                              boxShadow: 3
                            },
                            border: '1px solid',
                            borderColor: 'grey.200',
                            borderRadius: 2,
                            margin: '0 auto'
                          }}
                        >
                          {/* Remove button positioned at top-right */}
                          <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 2 }}>
                            <Tooltip title="Remove tag from this food" arrow>
                              <IconButton
                                size="small"
                                onClick={() => handleUnassignFromFood(food.id, viewingTag?.id)}
                                sx={{ 
                                  color: 'error.main',
                                  bgcolor: 'rgba(255, 255, 255, 0.9)',
                                  width: 24,
                                  height: 24,
                                  backdropFilter: 'blur(4px)',
                                  '&:hover': { 
                                    bgcolor: 'error.main',
                                    color: 'white',
                                    transform: 'scale(1.1)'
                                  }
                                }}
                              >
                                <ClearIcon sx={{ fontSize: '16px' }} />
                              </IconButton>
                            </Tooltip>
                          </Box>

                          <CardContent sx={{ 
                            p: 2, 
                            height: '100%', 
                            display: 'flex', 
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            textAlign: 'center',
                            '&:last-child': { pb: 2 } 
                          }}>
                            <Box sx={{ mb: 1.5, flexShrink: 0 }}>
                              <Avatar
                                src={food.image}
                                alt={food.name}
                                sx={{ 
                                  width: 60, 
                                  height: 60,
                                  borderRadius: 2,
                                  bgcolor: 'grey.100',
                                  mx: 'auto'
                                }}
                                variant="rounded"
                              >
                                🍽️
                              </Avatar>
                            </Box>
                            
                            <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', minHeight: 0, width: '100%' }}>
                              <Typography 
                                variant="subtitle2" 
                                sx={{ 
                                  fontWeight: 600,
                                  lineHeight: 1.2,
                                  mb: 0.5,
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                  overflow: 'hidden',
                                  wordBreak: 'break-word',
                                  fontSize: '0.75rem',
                                  height: '1.8rem',
                                  maxWidth: '100%'
                                }}
                                title={food.name}
                              >
                                {food.name}
                              </Typography>
                              
                              <Typography
                                variant="caption"
                                sx={{ 
                                  color: 'text.secondary',
                                  fontSize: '0.7rem',
                                  fontWeight: 500,
                                  textAlign: 'center',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}
                                title={food.category}
                              >
                                {food.category || 'No category'}
                              </Typography>
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}

              {/* Recipes Section */}
              {associations.recipes && associations.recipes.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography 
                    variant="h6" 
                    gutterBottom 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 1,
                      mb: 2,
                      color: 'secondary.main',
                      fontWeight: 600
                    }}
                  >
                    👨‍🍳 Recipes ({associations.recipes.length})
                  </Typography>
                  <Grid container spacing={2} sx={{ justifyContent: 'flex-start' }}>
                    {associations.recipes.map((recipe) => (
                      <Grid 
                        item 
                        xs={3} 
                        sm={3} 
                        md={3} 
                        lg={3}
                        key={recipe.id}
                        sx={{ 
                          display: 'flex',
                          '& > *': { 
                            flex: 1,
                            minWidth: 0 
                          }
                        }}
                      >
                        <Card 
                          sx={{ 
                            height: '180px',
                            width: '100%',
                            minWidth: '200px',
                            maxWidth: '200px',
                            display: 'flex',
                            flexDirection: 'column',
                            position: 'relative',
                            transition: 'all 0.2s ease-in-out',
                            '&:hover': { 
                              transform: 'translateY(-2px)',
                              boxShadow: 3
                            },
                            border: '1px solid',
                            borderColor: 'grey.200',
                            borderRadius: 2,
                            margin: '0 auto'
                          }}
                        >
                          {/* Remove button positioned at top-right */}
                          <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 2 }}>
                            <Tooltip title="Remove tag from this recipe" arrow>
                              <IconButton
                                size="small"
                                onClick={() => handleUnassignFromRecipe(recipe.id, viewingTag?.id)}
                                sx={{ 
                                  color: 'error.main',
                                  bgcolor: 'rgba(255, 255, 255, 0.9)',
                                  width: 24,
                                  height: 24,
                                  backdropFilter: 'blur(4px)',
                                  '&:hover': { 
                                    bgcolor: 'error.main',
                                    color: 'white',
                                    transform: 'scale(1.1)'
                                  }
                                }}
                              >
                                <ClearIcon sx={{ fontSize: '16px' }} />
                              </IconButton>
                            </Tooltip>
                          </Box>

                          <CardContent sx={{ 
                            p: 2, 
                            height: '100%', 
                            display: 'flex', 
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            textAlign: 'center',
                            '&:last-child': { pb: 2 } 
                          }}>
                            <Box sx={{ mb: 1.5, flexShrink: 0 }}>
                              <Avatar
                                src={recipe.image}
                                alt={recipe.title}
                                sx={{ 
                                  width: 60, 
                                  height: 60,
                                  borderRadius: 2,
                                  bgcolor: 'grey.100',
                                  mx: 'auto'
                                }}
                                variant="rounded"
                              >
                                🍳
                              </Avatar>
                            </Box>
                            
                            <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', minHeight: 0, width: '100%' }}>
                              <Typography 
                                variant="subtitle2" 
                                sx={{ 
                                  fontWeight: 600,
                                  lineHeight: 1.2,
                                  mb: 0.5,
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                  overflow: 'hidden',
                                  wordBreak: 'break-word',
                                  fontSize: '0.75rem',
                                  height: '1.8rem',
                                  maxWidth: '100%'
                                }}
                                title={recipe.title}
                              >
                                {recipe.title}
                              </Typography>
                              
                              <Typography
                                variant="caption"
                                sx={{ 
                                  color: 'text.secondary',
                                  fontSize: '0.7rem',
                                  fontWeight: 500,
                                  textAlign: 'center',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}
                                title={recipe.servings ? `${recipe.servings} servings` : 'No serving info'}
                              >
                                {recipe.servings ? `${recipe.servings} servings` : 'No serving info'}
                              </Typography>
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}

              {/* No associations message */}
              {(!associations.foods || associations.foods.length === 0) && 
               (!associations.recipes || associations.recipes.length === 0) && (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                  <Typography variant="h6" color="textSecondary" gutterBottom>
                    No Associated Items
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    No foods or recipes are currently associated with this tag.
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={() => setViewDialogOpen(false)}
            variant="contained"
            sx={{ px: 4 }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TagManager;